import { rangeTypes } from '@dataplace.ai/constants'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import NumberFormat from 'react-number-format'
import { useCheckForDesktopMediumDevice, useCheckForMobileDevice } from '@dataplace.ai/ui-components/themes'
import CatchmentInfoComponent from '../components/CatchmentInfoComponent'
import { StyledWrapper, TitleWrapper } from '../SharedOverview.styles'
import { NO_OVERVIEW_DATA } from '../constants'
import { NoDataGuard } from '../components/NoDataGuard'

const DataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const DataName = styled.p(({
  theme: {
    palette, typography,
  },
}) => css`
  margin: 0;
  color: ${palette.black};
  font-weight: ${typography.tiny.pt_12_bold.fontWeight};
  font-size: ${typography.tiny.pt_12_bold.fontSize};
  line-height: ${typography.tiny.pt_12_bold.lineHeight};
`)

const DataValue = styled.h2(({
  theme: {
    palette, typography,
  },
}) => css`
  margin: 0;
  color: ${palette.black};
  font-weight: ${typography.big.pt_18_semibold.fontWeight};
  font-size: ${typography.big.pt_18_semibold.fontSize};
  line-height: ${typography.big.pt_18_semibold.lineHeight};
`)

const ContentWrapper = styled.div`
  gap: 0.25rem;
`

interface OverviewWeekTrafficProps {
  catchment: {
    mode: (typeof rangeTypes)[keyof typeof rangeTypes]
    length: number
  }
  weekParts: {
    weekdays: number
    weekends: number
  }
}

export type OverviewWeekTrafficData = OverviewWeekTrafficProps | typeof NO_OVERVIEW_DATA
const TILE_TITLE_KEY = 'placeme.analyse.overview.week_traffic.tile_name'

function OVERVIEW_WEEK_TRAFFIC({
  catchment: {
    mode, length,
  },
  weekParts: {
    weekdays, weekends,
  },
}: OverviewWeekTrafficProps): JSX.Element {
  const { t } = useTranslation()
  const mediumDesktop = useCheckForDesktopMediumDevice()
  const mobile = useCheckForMobileDevice()
  const mediumOrMobile = mediumDesktop || mobile

  return (
    <StyledWrapper $width={mediumOrMobile ? '283px' : undefined}>
      <TitleWrapper>
        {t(TILE_TITLE_KEY)}
        <CatchmentInfoComponent
          length={length}
          mode={mode}
        />
      </TitleWrapper>
      <ContentWrapper>
        <DataWrapper>
          <DataName>{t('placeme.analyse.overview.week_traffic.weekdays')}</DataName>
          <DataValue>
            <NumberFormat
              displayType='text'
              thousandSeparator=' '
              value={weekdays}
            />
          </DataValue>
        </DataWrapper>
        <DataWrapper>
          <DataName>{t('placeme.analyse.overview.week_traffic.weekends')}</DataName>
          <DataValue>
            <NumberFormat
              displayType='text'
              thousandSeparator=' '
              value={weekends}
            />
          </DataValue>
        </DataWrapper>
      </ContentWrapper>
    </StyledWrapper>
  )
}

export function OverviewWeekTraffic({ data }: {data: OverviewWeekTrafficData}): JSX.Element {
  return (
    <NoDataGuard
      component={OVERVIEW_WEEK_TRAFFIC}
      data={data}
      title={TILE_TITLE_KEY}
    />
  )
}
